import React, {useEffect, useRef, useState} from "react";
import {useOfficeTranslation} from "i18n/OfficeTranslation";

const THRESHOLD = 80;
const MAX_PULL = 110;

function isEditableTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) {
        return false;
    }

    const tagName = target.tagName.toLowerCase();
    return tagName === "input" || tagName === "textarea" || tagName === "select" || target.isContentEditable;
}

function getScrollTop(): number {
    return window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
}

const PullToRefresh: React.FC = () => {
    const {t} = useOfficeTranslation();
    const [pullDistance, setPullDistance] = useState(0);
    const [refreshing, setRefreshing] = useState(false);
    const startY = useRef(0);
    const startX = useRef(0);
    const pullDistanceRef = useRef(0);
    const refreshingRef = useRef(false);
    const tracking = useRef(false);
    const horizontalGesture = useRef(false);

    useEffect(() => {
        pullDistanceRef.current = pullDistance;
    }, [pullDistance]);

    useEffect(() => {
        refreshingRef.current = refreshing;
    }, [refreshing]);

    useEffect(() => {
        const handleTouchStart = (event: TouchEvent) => {
            if (refreshingRef.current || event.touches.length !== 1 || getScrollTop() > 0 || isEditableTarget(event.target)) {
                tracking.current = false;
                return;
            }

            const touch = event.touches[0];
            startY.current = touch.clientY;
            startX.current = touch.clientX;
            horizontalGesture.current = false;
            tracking.current = true;
        };

        const handleTouchMove = (event: TouchEvent) => {
            if (!tracking.current || event.touches.length !== 1) {
                return;
            }

            const touch = event.touches[0];
            const deltaY = touch.clientY - startY.current;
            const deltaX = Math.abs(touch.clientX - startX.current);

            if (deltaX > Math.abs(deltaY) && deltaX > 12) {
                horizontalGesture.current = true;
                setPullDistance(0);
                return;
            }

            if (horizontalGesture.current || deltaY <= 0 || getScrollTop() > 0) {
                setPullDistance(0);
                return;
            }

            event.preventDefault();
            setPullDistance(Math.min(MAX_PULL, deltaY * 0.55));
        };

        const handleTouchEnd = () => {
            if (!tracking.current) {
                return;
            }

            const shouldRefresh = pullDistanceRef.current >= THRESHOLD;
            tracking.current = false;
            horizontalGesture.current = false;

            if (!shouldRefresh) {
                setPullDistance(0);
                return;
            }

            setRefreshing(true);
            setPullDistance(THRESHOLD);

            navigator.serviceWorker.getRegistration()
                .then(registration => registration?.update())
                .catch(() => undefined)
                .finally(() => {
                    window.location.reload();
                });
        };

        window.addEventListener("touchstart", handleTouchStart, {passive: true});
        window.addEventListener("touchmove", handleTouchMove, {passive: false});
        window.addEventListener("touchend", handleTouchEnd);
        window.addEventListener("touchcancel", handleTouchEnd);

        return () => {
            window.removeEventListener("touchstart", handleTouchStart);
            window.removeEventListener("touchmove", handleTouchMove);
            window.removeEventListener("touchend", handleTouchEnd);
            window.removeEventListener("touchcancel", handleTouchEnd);
        };
    }, []);

    if (pullDistance <= 0 && !refreshing) {
        return null;
    }

    const progress = Math.min(1, pullDistance / THRESHOLD);

    return (
        <div
            aria-live="polite"
            style={{
                alignItems: "center",
                background: "rgba(18, 30, 45, 0.94)",
                borderRadius: "999px",
                color: "#fff",
                display: "flex",
                fontSize: "12px",
                fontWeight: 600,
                gap: "8px",
                left: "50%",
                padding: "8px 12px",
                pointerEvents: "none",
                position: "fixed",
                top: `${Math.max(12, pullDistance - 42)}px`,
                transform: "translateX(-50%)",
                zIndex: 10000,
            }}
        >
            <span
                className={refreshing ? "mdi mdi-refresh mdi-spin" : "mdi mdi-arrow-down"}
                style={{
                    display: "inline-block",
                    opacity: refreshing ? 1 : 0.55 + progress * 0.45,
                    transform: refreshing ? undefined : `rotate(${progress * 180}deg)`,
                }}
            />
            <span>{refreshing ? t("common.pullToRefresh.refreshing") : progress >= 1 ? t("common.pullToRefresh.releaseToRefresh") : t("common.pullToRefresh.pullToRefresh")}</span>
        </div>
    );
};

export default PullToRefresh;
